/**
 * Canonical AppSync event-type names — mirror of `stepflow_core.events.EVENT_TYPES`.
 *
 * The backend stamps every envelope with one of these strings under `eventType`
 * and the room / runner / inbox UIs match against the constants below. Keeping
 * one shared list means a grep for any name lands in both repos and removes
 * the string-typo class of bug we hit when we sprinkled literals through
 * `AgenticNetworkRoom.jsx`, `useApprovalClaim.js`, `RunnerView.jsx`, etc.
 *
 * Add a new value here only when the backend `EVENT_TYPES` set adds it. The
 * `LOG_BATCH` value is *not* in the backend enum but is published over the
 * same transport by `stepflow_core.logger`; keep it grouped here so the
 * frontend has a single source of truth for the wire vocabulary.
 */

export const AGENT_EVENTS = Object.freeze({
  // Lifecycle ---------------------------------------------------------------
  EXECUTION_STARTED: 'EXECUTION_STARTED',
  EXECUTION_COMPLETED: 'EXECUTION_COMPLETED',
  EXECUTION_FAILED: 'EXECUTION_FAILED',
  SFN_TRANSITION: 'SFN_TRANSITION',
  STATUS_UPDATE: 'STATUS_UPDATE',

  // Conversation / agent reasoning -----------------------------------------
  USER_PROMPT: 'USER_PROMPT',
  SUPERVISOR_MESSAGE: 'SUPERVISOR_MESSAGE',
  AGENT_MESSAGE: 'AGENT_MESSAGE',
  AGENT_TOOL_CALL: 'AGENT_TOOL_CALL',
  /** Generative-engine reasoning (Lead routing rationale + node-reducer accept/keep-going
   *  decision), streamed to the room as an AMBIENT thinking line — no agent badge, ephemeral. */
  AGENT_REASONING: 'AGENT_REASONING',
  /** Final-synthesis STREAM — the final reducer emits throttled chunks of the answer as it composes
   *  so the room's Summary block fills in live (no ~45s static "synthesizing…" lull at the climax).
   *  Ephemeral; the authoritative AGENT_FINAL_RESPONSE(Summary) replaces the streamed preview. */
  SUMMARY_DELTA: 'SUMMARY_DELTA',
  /** Per-agent KICKOFF beat — a worker/builder publishes this the instant it begins, carrying its
   *  agentId + tree position (nodePath/level). Gives the room's roster a ``running`` transition
   *  (spinner + blue graph tint) BEFORE the final response, so a seeded ``standby`` row doesn't jump
   *  straight to ``done``. The engine equivalent of Brain's intermediate GENERATING_* stages. */
  AGENT_STARTED: 'AGENT_STARTED',
  AGENT_FINAL_RESPONSE: 'AGENT_FINAL_RESPONSE',
  /** Objective-judge verdict for a scheduler loop turn (SCHEDULER.md §11.1b) —
   *  streamed to the room as a distinct "Judge" voice. */
  JUDGE_VERDICT: 'JUDGE_VERDICT',
  LOG_LINE: 'LOG_LINE',
  /** Batched log delivery from `stepflow_core.logger` — same transport, not in backend EVENT_TYPES. */
  LOG_BATCH: 'LOG_BATCH',
  /** Universal Builder paused — room UI should POST a CLI line to resume the PTY. */
  BUILDER_INPUT_WAIT: 'BUILDER_INPUT_WAIT',
  /** Builder prompt was resolved — peer subscribers dismiss the interaction band. */
  BUILDER_INPUT_RESOLVED: 'BUILDER_INPUT_RESOLVED',
  /** Builder progress phase — provisioning, repo clone, claude code starting/running, completed. */
  BUILDER_PHASE: 'BUILDER_PHASE',
  /** Builder run control — e.g. user clicked Stop; peers clear the Stop button. */
  BUILDER_RUN_CONTROL: 'BUILDER_RUN_CONTROL',

  // UI orchestration -------------------------------------------------------
  UI_STATE: 'UI_STATE',

  // Network release / membership -------------------------------------------
  NETWORK_RELEASED: 'NETWORK_RELEASED',
  MEMBERSHIP_GRANTED: 'MEMBERSHIP_GRANTED',
  MEMBERSHIP_REVOKED: 'MEMBERSHIP_REVOKED',
  MEMBERSHIP_SELF_ELEVATED: 'MEMBERSHIP_SELF_ELEVATED',
  POOL_STATUS_CHANGED: 'POOL_STATUS_CHANGED',

  // Human-in-loop approvals ------------------------------------------------
  APPROVAL_REQUESTED: 'APPROVAL_REQUESTED',
  APPROVAL_RESOLVED: 'APPROVAL_RESOLVED',
  APPROVAL_CLAIMED: 'APPROVAL_CLAIMED',
  APPROVAL_DELEGATED: 'APPROVAL_DELEGATED',
  APPROVAL_ESCALATED: 'APPROVAL_ESCALATED',
  APPROVAL_CLAIM_EXPIRED: 'APPROVAL_CLAIM_EXPIRED',

  // Presence / collaboration ----------------------------------------------
  USER_PRESENCE_JOINED: 'USER_PRESENCE_JOINED',
  USER_PRESENCE_LEFT: 'USER_PRESENCE_LEFT',
  USER_TYPING: 'USER_TYPING',
  PRESENCE_SNAPSHOT: 'PRESENCE_SNAPSHOT',
  WORKBENCH_PRESENCE_JOINED: 'WORKBENCH_PRESENCE_JOINED',

  // Personal notifications -------------------------------------------------
  USER_NOTIFICATION: 'USER_NOTIFICATION',
});

/** Stable key set for `Set.has` / `switch` exhaustiveness. */
export const AGENT_EVENT_NAMES = Object.freeze(Object.values(AGENT_EVENTS));

/** Subset that participates in the approval lifecycle (used by `useApprovalClaim`). */
export const APPROVAL_EVENT_TYPES = Object.freeze([
  AGENT_EVENTS.APPROVAL_REQUESTED,
  AGENT_EVENTS.APPROVAL_CLAIMED,
  AGENT_EVENTS.APPROVAL_DELEGATED,
  AGENT_EVENTS.APPROVAL_ESCALATED,
  AGENT_EVENTS.APPROVAL_CLAIM_EXPIRED,
  AGENT_EVENTS.APPROVAL_RESOLVED,
]);

/** Subset that produces visible chat content in the agentic room. */
export const CHAT_DISPLAY_EVENT_TYPES = Object.freeze([
  AGENT_EVENTS.AGENT_FINAL_RESPONSE,
  AGENT_EVENTS.JUDGE_VERDICT,
  AGENT_EVENTS.AGENT_MESSAGE,
  AGENT_EVENTS.SUPERVISOR_MESSAGE,
  AGENT_EVENTS.USER_PROMPT,
]);

/** Subset that drives the per-turn "Thinking" stream (Phase 2). */
export const THINKING_STREAM_EVENT_TYPES = Object.freeze([
  AGENT_EVENTS.STATUS_UPDATE,
  AGENT_EVENTS.LOG_LINE,
  AGENT_EVENTS.LOG_BATCH,
  AGENT_EVENTS.AGENT_TOOL_CALL,
  AGENT_EVENTS.AGENT_REASONING,
  AGENT_EVENTS.SFN_TRANSITION,
]);

/** UI-state values published under `payload.uiState` of an `UI_STATE` envelope. */
export const UI_STATES = Object.freeze({
  IDLE: 'IDLE',
  RUNNING: 'RUNNING',
  AWAITING_APPROVAL: 'AWAITING_APPROVAL',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
});

/**
 * Pull a normalized event-type from any envelope shape we see on the wire.
 *
 * The backend uses `eventType`; some legacy and runner-side payloads use
 * `type`. Returns the empty string when neither field is a non-empty string
 * so callers can use truthy checks before switching.
 */
export function envelopeEventType(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const a = typeof raw.eventType === 'string' ? raw.eventType : '';
  if (a) return a.trim();
  const b = typeof raw.type === 'string' ? raw.type : '';
  return b.trim();
}

/** Returns `true` when the envelope's `eventType` matches `name`, case-sensitive. */
export function isAgentEvent(raw, name) {
  return envelopeEventType(raw) === name;
}
